import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, theme, Typography, Avatar, Dropdown, Space } from 'antd';
import {
  TeamOutlined,
  RocketOutlined,
  GlobalOutlined,
  CalendarOutlined,
  SendOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';

import { useAuth } from '../hooks/useAuth';

const { Sider, Header, Content } = Layout;
const { Title, Text } = Typography;

export function ShellLayout() {
  const { token: themeToken } = theme.useToken();
  const nav = useNavigate();
  const loc = useLocation();
  const admin = useAuth((s) => s.admin);
  const clear = useAuth((s) => s.clear);
  // Admin regression 6 Oct — below the lg breakpoint the sider collapses to
  // 64px, and the wordmark ("ShareJet ADMIN PANEL") wrapped one letter per
  // line in it. Collapsed, only the SJ badge is shown, as the menu shows only
  // its icons.
  const [collapsed, setCollapsed] = useState(false);

  const items = [
    // SHAR-1037 — this order, Bookings first and the default after login.
    { key: '/bookings', icon: <CalendarOutlined />, label: 'Bookings' },
    // SHAR-118 — charter bookings are their own list, as ShareJet bookings are.
    { key: '/charter-bookings', icon: <CalendarOutlined />, label: 'Charter bookings' },
    { key: '/charters', icon: <SendOutlined />, label: 'Charters' },
    { key: '/aircraft', icon: <RocketOutlined />, label: 'Aircraft' },
    { key: '/operators', icon: <TeamOutlined />, label: 'Operators' },
    { key: '/airports', icon: <GlobalOutlined />, label: 'Airports' },
    { key: '/settings', icon: <SettingOutlined />, label: 'Settings' },
  ];

  const selected = items.find((i) => loc.pathname.startsWith(i.key))?.key || '/bookings';

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        width={232}
        style={{ background: '#0F2A4A' }}
        breakpoint="lg"
        collapsedWidth={64}
        onCollapse={(c) => setCollapsed(c)}
      >
        <div style={{ padding: collapsed ? '20px 18px 14px 18px' : '20px 16px 14px 16px' }}>
          <Space align="center" size={10}>
            <div
              style={{
                width: 28,
                height: 28,
                background: '#E8F0FF',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                color: '#0F2A4A',
              }}
            >
              SJ
            </div>
            {collapsed ? null : (
              <Title level={5} style={{ color: '#fff', margin: 0 }}>
                ShareJet
              </Title>
            )}
          </Space>
          {collapsed ? null : (
            <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, letterSpacing: 1, marginLeft: 8 }}>
              ADMIN PANEL
            </Text>
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          style={{ background: 'transparent', borderRight: 0 }}
          selectedKeys={[selected]}
          items={items.map((i) => ({ key: i.key, icon: i.icon, label: i.label }))}
          onClick={({ key }) => nav(key)}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            background: themeToken.colorBgContainer,
            paddingInline: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            borderBottom: `1px solid ${themeToken.colorBorderSecondary}`,
          }}
        >
          <Dropdown
            placement="bottomRight"
            menu={{
              items: [
                {
                  key: 'logout',
                  icon: <LogoutOutlined />,
                  label: 'Log out',
                  onClick: () => {
                    clear();
                    nav('/login');
                  },
                },
              ],
            }}
          >
            {/* Admin regression 6 Oct — one flex row, centred on the header's
                axis: inside a Space the ellipsis Text inherited the header's
                64px line height and sat below the avatar, against the bottom
                edge. */}
            <div
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                maxWidth: 240,
                minWidth: 0,
                lineHeight: 'normal',
              }}
            >
              <Avatar size="small" icon={<UserOutlined />} style={{ flex: 'none' }} />
              <Text ellipsis={{ tooltip: admin?.fullName || admin?.email }} style={{ maxWidth: 200, minWidth: 0 }}>
                {admin?.fullName || admin?.email || 'Admin'}
              </Text>
            </div>
          </Dropdown>
        </Header>
        <Content style={{ padding: 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
